import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import {
  ExternalLink,
  Github,
  Code,
  Database,
  Smartphone,
  ShoppingCart,
} from "lucide-react";

const Projects = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.1 });
  const [hoveredCard, setHoveredCard] = useState(null);

  const projects = [
    {
      title: "PS Portal",
      shortTitle: "PS Portal – Skill Learning & Assessment System",
      description:
        "Comprehensive Programming Skills Management System for BITSathy. Full-stack platform for course learning, coding practice, and assessments.",
      hoverDescription:
        "PS Portal is a full-stack Programming Skills Management System built for BITSathy. It supports structured learning modules, coding practice, secure assessments, auto-grading, and real-time analytics. The platform uses Go (Gin), React, MySQL, and Redis caching for high performance. Integrated with Google & Microsoft SSO, Safe Exam Browser, Docker-based deployment, and Cloudflare SSL protection. Designed to scale for 10,000+ users with robust backend architecture and microservices.",
      domain: "https://ps.bitsathy.ac.in",
      technologies: ["React", "Go (Gin)", "MySQL", "Redis", "Docker"],
      icon: Code,
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      category: "Education Platform",
    },
    {
      title: "College ERP Portal",
      shortTitle: "College ERP – Academic & Examination Automation",
      description:
        "Academic management system for BITSathy to manage course coordination, attendance, and examinations.",
      hoverDescription:
        "A complete academic ERP system for BITSathy that manages course administration, attendance, question banks, examinations, seating arrangement, and mark entry. Built using React.js, Go (Gin), and MySQL with role-based access, Google OAuth authentication, PDF/Excel generation, and multi-level approval workflows. Automates end-to-end COE operations, improving efficiency and accuracy across departments.",
      domain: "https://learnathon.bitsathy.ac.in",
      technologies: ["React.js", "Go (Gin)", "MySQL", "OAuth", "Docker"],
      icon: Database,
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      category: "Academic System",
    },
    {
      title: "PS Student App (PCDP App)",
      shortTitle: "PS Student App – Activity & Attendance System",
      description:
        "React Native mobile app for student activity & attendance with offline-first architecture.",
      hoverDescription:
        "A React Native mobile application integrated with a Go backend for managing student activities, QR-based attendance, leave workflows, surveys, announcements, and gamified rewards. Features offline-first design using SQLite, background sync, offline QR generation/scanning, Firebase push notifications, and multi-organization branding. Built for seamless student engagement, even in low-network environments.",
      domain: "Play Store: PCDP App",
      technologies: ["React Native", "SQLite", "Firebase", "Go", "GORM"],
      icon: Smartphone,
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      category: "Mobile Application",
    },
    {
      title: "One-Click Deploy Tool",
      shortTitle: "One-Click Deploy – Automated Deployment System",
      description:
        "A Go-based automation tool that deploys full PS Portal stacks with a single click.",
      hoverDescription:
        "A fully automated deployment system built entirely in Go (no frameworks) that enables one-click deployment of the complete PS Portal application. Includes a modern web UI, real-time log streaming (SSE), auto .env generation, MySQL database creation and import, Docker Compose generation, and atomic single-run protection. Supports Microsoft SSO, Cloudflare tunnels, and production-grade multi-domain server environments.",
      domain: "Internal DevOps Tool",
      technologies: [
        "Go",
        "Docker Compose",
        "MySQL",
        "Nginx",
      ],
      icon: Code,
      gradient: "linear-gradient(135deg, #7D53F6 0%, #4C1D95 100%)",
      category: "DevOps / Automation",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
      },
    },
  };

  const projectVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <section id="projects" className="projects section">
      <div className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.h2 className="section-title" variants={projectVariants}>
            Featured Projects
          </motion.h2>

          <div className="projects-grid">
            {projects.map((project, index) => {
              const IconComponent = project.icon;
              return (
                <motion.div
                  key={project.title}
                  className="project-card"
                  variants={projectVariants}
                  onHoverStart={() => setHoveredCard(index)}
                  onHoverEnd={() => setHoveredCard(null)}
                  whileHover={{
                    y: -10,
                    scale: 1.02,
                    transition: { duration: 0.3 },
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div
                    className="project-gradient-bg"
                    style={{ background: project.gradient }}
                  />

                  <div className="project-icon-wrapper">
                    <motion.div
                      className="project-icon"
                      animate={{
                        rotate: hoveredCard === index ? 360 : 0,
                        scale: hoveredCard === index ? 1.1 : 1,
                      }}
                      transition={{ duration: 0.5 }}
                    >
                      <IconComponent size={32} />
                    </motion.div>
                  </div>

                  <div className="project-content">
                    <div className="project-header">
                      <span className="project-category">
                        {project.category}
                      </span>
                      <h3 className="project-title">{project.title}</h3>
                      <a
                        href={project.domain}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-domain"
                      >
                        <ExternalLink size={16} />
                        {project.domain.replace("https://", "")}
                      </a>
                    </div>

                    <motion.div
                      className="project-description-wrapper"
                      animate={{
                        height: hoveredCard === index ? "auto" : "80px",
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      <p className="project-description">
                        {hoveredCard === index
                          ? project.hoverDescription
                          : project.description}
                      </p>
                    </motion.div>

                    <div className="project-tech">
                      {project.technologies.map((tech, techIndex) => (
                        <motion.span
                          key={techIndex}
                          className="tech-tag"
                          whileHover={{
                            scale: 1.05,
                            y: -2,
                          }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                          {tech}
                        </motion.span>
                      ))}
                    </div>

                    <motion.a
                      href={project.domain}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="learn-more-btn"
                      whileHover={{
                        scale: 1.05,
                        boxShadow: "0 8px 25px rgba(125, 83, 246, 0.3)",
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      View Project
                      <ExternalLink size={18} />
                    </motion.a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
